import React, { useMemo } from 'react';
import { Box, Typography, LinearProgress, Card, CardContent, Grid } from '@mui/material';
import { Bar } from 'react-chartjs-2';
import AnimatedBloqcubeLogo from '../../../components/Logo/AnimatedBloqcubeLogo';
import { useFinanceDashboradDataQuery } from '../../../store/slices/apiSlice';

const SiteExpenditure = ({ clinicalStudyId, studySiteIds, ...props }) => {
  const { data: dashboardData, isLoading } = useFinanceDashboradDataQuery({
    data: {
      preference: props.preference,
      clinicalStudyId,
      studySiteIds: studySiteIds?.join(','),
    },
  });

  const { chartData, summaryData, hasData } = useMemo(() => {
    if (!dashboardData?.budgetBreakdown) {
      return { chartData: null, summaryData: null, hasData: false };
    }

    const { budgetBreakdown } = dashboardData;
    const { sitesBudget, totalStudyBudget } = budgetBreakdown;

    // Calculate total expenditure from all sites (convert cents to dollars)
    const totalExpenditure = sitesBudget.reduce((total, site) => {
      const siteExpenditure = site.siteExpenditure.reduce((siteTotal, exp) => {
        return siteTotal + parseFloat(exp.totalAmount) / 100; // Convert cents to dollars
      }, 0);
      return total + siteExpenditure;
    }, 0);

    // Prepare chart data
    const siteNames = [];
    const siteBudgets = [];
    const siteExpenditures = [];

    sitesBudget.forEach((site) => {
      const expenditure = site.siteExpenditure.reduce((total, exp) => {
        return total + parseFloat(exp.totalAmount) / 100; // Convert cents to dollars
      }, 0);

      siteNames.push(site.siteName);
      siteBudgets.push(site.totalSiteBudget);
      siteExpenditures.push(expenditure);
    });

    const chartData = {
      labels: siteNames,
      datasets: [
        {
          label: 'Budget ($)',
          data: siteBudgets,
          backgroundColor: '#E3F2FD',
          borderColor: '#1976D2',
          borderWidth: 2,
        },
        {
          label: 'Expenditure ($)',
          data: siteExpenditures,
          backgroundColor: '#FFF3E0',
          borderColor: '#F57C00',
          borderWidth: 2,
        },
      ],
    };

    const summaryData = {
      totalBudget: totalStudyBudget,
      totalExpenditure,
      remainingBudget: totalStudyBudget - totalExpenditure,
      utilizationPercentage: totalStudyBudget > 0 ? (totalExpenditure / totalStudyBudget) * 100 : 0,
      sites: sitesBudget.map((site) => ({
        name: site.siteName,
        budget: site.totalSiteBudget,
        expenditure: site.siteExpenditure.reduce((total, exp) => {
          return total + parseFloat(exp.totalAmount) / 100;
        }, 0),
        transactionCount: site.siteExpenditure.reduce((total, exp) => {
          return total + exp.transactionCount;
        }, 0),
      })),
    };

    return {
      chartData,
      summaryData,
      hasData: sitesBudget.length > 0 || totalStudyBudget > 0,
    };
  }, [dashboardData]);

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          padding: 20,
          usePointStyle: true,
          font: {
            size: 12,
          },
        },
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            return `${context.dataset.label}: $${context.parsed.y.toLocaleString()}`;
          },
        },
      },
      datalabels: {
        display: false,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: function (value) {
            return '$' + value.toLocaleString();
          },
        },
      },
    },
  };

  // Format currency helper
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  // No data state
  const NoDataDisplay = () => (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        textAlign: 'center',
        color: 'text.secondary',
      }}
    >
      <Typography variant="h6" sx={{ mb: 1 }}>
        No Budget Data
      </Typography>
      <Typography variant="body2">No budget or expenditure data found for the selected criteria.</Typography>
    </Box>
  );

  return (
    <Box
      onMouseDown={(e) => e.stopPropagation()}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        position: 'relative',
        p: 2,
      }}
    >
      {isLoading && (
        <Box
          sx={{
            height: '100%',
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Box sx={{ height: 250, width: 250 }}>
            <AnimatedBloqcubeLogo />
          </Box>
        </Box>
      )}

      {!isLoading && dashboardData && (
        <>
          {hasData ? (
            <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              {/* Chart */}
              <Box sx={{ flex: 1, minHeight: 300 }}>
                <Bar data={chartData} options={chartOptions} />
              </Box>
            </Box>
          ) : (
            <NoDataDisplay />
          )}
        </>
      )}
    </Box>
  );
};

export default SiteExpenditure;
